import { Link } from 'react-router-dom';
import { formatCffSubmittedByLabel, statusClass, STATUS_LABEL } from '../../lib/accountCreationUi';
import {
  cffWarehouseRolesStatus,
  cffWarehouseRolesStatusLabel,
} from '../../lib/customerFeedbackSubmissions';
import type { ApplicationSubmission, CustomerFeedbackFormData } from '../../types/applicationForm';

function requestTypeLabel(type: CustomerFeedbackFormData['request_type']): string {
  if (type === 'return') return 'Return';
  if (type === 'exchange') return 'Exchange';
  if (type === 'replenish') return 'Replenish';
  return '—';
}

function itemCodes(data: CustomerFeedbackFormData | undefined): string[] {
  return (data?.items ?? [])
    .map((row) => String(row.item_code ?? '').trim())
    .filter(Boolean);
}

type CffListRowProps = {
  row: ApplicationSubmission;
  detailPath: string;
  duplicateInvoice?: boolean;
};

export function CffListRow({ row, detailPath, duplicateInvoice = false }: CffListRowProps) {
  const data = row.form_data as CustomerFeedbackFormData;
  const req = data?.request_type || '';
  const invoice = data?.invoice_no?.trim() || '';
  const codes = itemCodes(data);
  const rolesStatus = cffWarehouseRolesStatus(data);
  const submittedBy = formatCffSubmittedByLabel(
    data?.submitted_by?.trim() || row.submitted_by,
    data?.invoice_issued_by,
  );

  return (
    <Link to={detailPath} className="cff-list-row">
      <span className="cff-list-row__ncpar-cell">
        <span className="cff-list-row__ncpar">{row.application_no}</span>
        <span className={statusClass(row.status)}>{STATUS_LABEL[row.status]}</span>
      </span>

      <span className="cff-list-row__company">
        <span className="cff-list-row__company-name">{row.company_name || '—'}</span>
        {data?.company_code ? (
          <span className="cff-list-row__company-code">{data.company_code}</span>
        ) : null}
      </span>

      <span className={`cff-request-badge cff-request-badge--${req || 'none'}`}>
        {requestTypeLabel(req)}
      </span>

      <span className="cff-list-row__invoice">
        <span className="cff-list-row__text">{invoice || '—'}</span>
        {duplicateInvoice && invoice ? (
          <span className="cff-list-row__dup-tag">Duplicate</span>
        ) : null}
      </span>

      <span className="cff-list-row__text cff-list-row__text--items">
        {codes.length > 0 ? (
          <span className="cff-list-row__items">
            {codes.map((code, index) => (
              <span key={`${code}-${index}`} className="cff-list-row__item">
                {code}
              </span>
            ))}
          </span>
        ) : (
          '—'
        )}
      </span>

      <span className="cff-list-row__text cff-list-row__text--reason">
        {(data?.reason ?? '').trim() || '—'}
      </span>

      <span className="cff-list-row__text cff-list-row__text--remarks">
        {(data?.internal_remarks ?? '').trim() || '—'}
      </span>

      <span className={`cff-roles-badge cff-roles-badge--${rolesStatus}`}>
        {cffWarehouseRolesStatusLabel(rolesStatus)}
      </span>

      <span className="cff-list-row__text cff-list-row__text--submitted">{submittedBy}</span>

      <span className="cff-list-row__chevron" aria-hidden>
        ›
      </span>
    </Link>
  );
}
